import React, { useState, useRef, useEffect } from "react";
import type { AccountInfo } from "@azure/msal-browser";
import { useProfilePhoto } from "../hooks/useProfilePhoto";
import type { ThemePreference } from "../hooks/useThemePreference";
import AppearanceSelector from "./AppearanceSelector";

interface UserProfileMenuProps {
  account: AccountInfo;
  onSignOut: () => void;
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (preference: ThemePreference) => void;
  canViewImpactDashboard?: boolean;
  onOpenImpactDashboard?: () => void;
}

/**
 * User profile avatar + dropdown menu with sign-out.
 * Shows profile photo from Microsoft Graph, with initials fallback.
 */
export default function UserProfileMenu({
  account,
  onSignOut,
  themePreference,
  onThemePreferenceChange,
  canViewImpactDashboard = false,
  onOpenImpactDashboard,
}: UserProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const photoUrl = useProfilePhoto();

  const displayName = account.name ?? account.username ?? "User";
  const email = account.username ?? "";
  const initials = getInitials(displayName);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={menuRef} style={containerStyle} data-app-usage-exclude>
      <button
        onClick={() => setOpen((prev) => !prev)}
        style={avatarButtonStyle}
        aria-label="User menu"
        title={displayName}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={displayName}
            style={avatarImgStyle}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span style={avatarInitialsStyle}>{initials}</span>
        )}
      </button>

      {open && (
        <div style={dropdownStyle}>
          <div style={dropdownHeaderStyle}>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                style={dropdownAvatarStyle}
                referrerPolicy="no-referrer"
              />
            ) : (
              <span style={dropdownInitialsStyle}>{initials}</span>
            )}
            <div style={dropdownInfoStyle}>
              <strong style={{ fontSize: 14 }}>{displayName}</strong>
              {email && (
                <span style={{ fontSize: 12, color: "var(--color-gray-60)" }}>{email}</span>
              )}
            </div>
          </div>
          <div style={dropdownDividerStyle} />
          {themePreference && onThemePreferenceChange && (
            <>
              <div style={appearanceStyle}>
                <AppearanceSelector value={themePreference} onChange={onThemePreferenceChange} />
              </div>
              <div style={dropdownDividerStyle} />
            </>
          )}
          {canViewImpactDashboard && onOpenImpactDashboard && (
            <>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onOpenImpactDashboard();
                }}
                style={dashboardBtnStyle}
              >
                <span style={menuIconStyle} aria-hidden="true" data-testid="impact-dashboard-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="2" width="5" height="5" rx="1" fill="currentColor" />
                    <rect x="9" y="2" width="5" height="3" rx="1" fill="currentColor" />
                    <rect x="9" y="7" width="5" height="7" rx="1" fill="currentColor" />
                    <rect x="2" y="9" width="5" height="5" rx="1" fill="currentColor" />
                  </svg>
                </span>
                <span>Impact dashboard</span>
                <span style={adminChipStyle}>Admin</span>
              </button>
              <div style={dropdownDividerStyle} />
            </>
          )}
          <button
            type="button"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSignOut();
            }}
            style={signOutBtnStyle}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  position: "relative",
  display: "inline-block",
};

const avatarButtonStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  border: "2px solid var(--color-primary)",
  background: "none",
  padding: 0,
  cursor: "pointer",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const avatarImgStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "50%",
};

const avatarInitialsStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  background: "var(--island-bg-color)",
  color: "var(--text-primary-color)",
  borderRadius: 8,
  boxShadow: "var(--exms-shadow-card-hover)",
  minWidth: 260,
  zIndex: 10000,
  pointerEvents: "auto",
  overflow: "hidden",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const dropdownHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 16px 12px",
};

const dropdownAvatarStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
};

const dropdownInitialsStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 18,
  fontWeight: 600,
  flexShrink: 0,
};

const dropdownInfoStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  overflow: "hidden",
};

const dropdownDividerStyle: React.CSSProperties = {
  height: 1,
  background: "var(--dialog-border-color)",
  margin: "0 12px",
};

const appearanceStyle: React.CSSProperties = {
  padding: "4px 16px",
};

const signOutBtnStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 16px",
  border: "none",
  background: "none",
  textAlign: "left",
  cursor: "pointer",
  pointerEvents: "auto",
  fontSize: 14,
  color: "var(--text-primary-color)",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const dashboardBtnStyle: React.CSSProperties = {
  ...signOutBtnStyle,
  display: "flex",
  alignItems: "center",
  gap: 12,
  background: "var(--color-primary-light)",
  color: "var(--color-primary)",
  fontWeight: 400,
};

const menuIconStyle: React.CSSProperties = {
  display: "grid",
  width: 28,
  height: 28,
  flexShrink: 0,
  placeItems: "center",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 8,
  background: "var(--color-surface-low)",
};

const adminChipStyle: React.CSSProperties = {
  marginLeft: "auto",
  padding: "3px 7px",
  borderRadius: 999,
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: 1.4,
  textTransform: "uppercase",
};
